"use client";

import { useState, useEffect, useCallback } from "react";

// ========== Helpers ==========

const STORAGE_KEY_IOS = "wtta_pwa_ios_dismissed";
const STORAGE_KEY_ANDROID = "wtta_pwa_android_dismissed";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

// Hauteur réservée en bas de page quand le bandeau est affiché (via l'espaceur en
// flux rendu juste en dessous). Valeurs volontairement généreuses pour absorber les
// variations de taille de texte système (accessibilité) — un léger surplus d'espace
// est inoffensif, un déficit recouvrirait la dernière carte du contenu.
const IOS_BANNER_HEIGHT_PX = 148;
const ANDROID_BANNER_HEIGHT_PX = 72;

function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
}

function isAndroidChrome(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android/.test(ua) && /Chrome/.test(ua) && !/Edge|EdgA/.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    );
  } catch {
    return false;
  }
}

function canShow(key: string): boolean {
  if (typeof localStorage === "undefined") return true;
  try {
    const val = localStorage.getItem(key);
    if (!val) return true;
    const dismissedAt = parseInt(val, 10);
    if (isNaN(dismissedAt)) return true;
    return Date.now() - dismissedAt > DISMISS_DURATION_MS;
  } catch {
    return true;
  }
}

function dismissKey(key: string): void {
  try {
    localStorage.setItem(key, Date.now().toString());
  } catch { /* localStorage indisponible, on ignore */ }
}

// ========== Composant ==========

export default function PWAInstallBanner() {
  const [showIOS, setShowIOS] = useState(false);
  const [showAndroid, setShowAndroid] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  const dismissIOS = useCallback(() => {
    dismissKey(STORAGE_KEY_IOS);
    setShowIOS(false);
  }, []);

  const dismissAndroid = useCallback(() => {
    dismissKey(STORAGE_KEY_ANDROID);
    setShowAndroid(false);
  }, []);

  useEffect(() => {
    if (isStandalone()) return; // déjà installé

    // --- iOS ---
    if (isIOSSafari() && canShow(STORAGE_KEY_IOS)) {
      const t = setTimeout(() => setShowIOS(true), 1500);
      return () => clearTimeout(t);
    }

    // --- Android ---
    if (isAndroidChrome()) {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e);
        if (canShow(STORAGE_KEY_ANDROID)) {
          setShowAndroid(true);
        }
      };
      window.addEventListener("beforeinstallprompt", handler);
      return () => window.removeEventListener("beforeinstallprompt", handler);
    }
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      // annulé
    }
    setDeferredPrompt(null);
    setShowAndroid(false);
    dismissKey(STORAGE_KEY_ANDROID);
  };

  if (!showIOS && !showAndroid) return null;

  return (
    <>
      {/* Espaceur en flux normal : réserve la place du bandeau fixé en bas pour
          que le contenu puisse défiler au-dessus. Rendu uniquement quand le
          bandeau est affiché, et disparaît avec lui (aucun nettoyage à écrire).
          Pas de mutation d'état global, rien sur desktop, rien après dismiss. */}
      {showIOS && <div style={{ height: IOS_BANNER_HEIGHT_PX, flexShrink: 0 }} aria-hidden="true" />}
      {showAndroid && <div style={{ height: ANDROID_BANNER_HEIGHT_PX, flexShrink: 0 }} aria-hidden="true" />}

      {/* ===== Bannière iOS ===== */}
      {showIOS && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up"
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.4)",
          }}
        >
          <div className="flex items-start gap-3 px-4 py-3 max-w-lg mx-auto">
            {/* Icône */}
            <div className="shrink-0 mt-0.5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                style={{ backgroundColor: "#e00000" }}
              >
                ⏱
              </div>
            </div>

            {/* Instructions */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">
                Installez cette application
              </p>
              <ol className="mt-1.5 text-xs space-y-1" style={{ color: "#ccc" }}>
                <li>
                  1. Appuyez sur{" "}
                  <strong style={{ color: "#fff" }}>Partager 📤</strong>{" "}
                  <span style={{ color: "#888" }}>(barre du bas)</span>
                </li>
                <li>
                  2. Faites défiler →{" "}
                  <strong style={{ color: "#fff" }}>Sur l&apos;écran d&apos;accueil</strong>
                </li>
                <li>
                  3. Appuyez sur{" "}
                  <strong style={{ color: "#fff" }}>Ajouter</strong>
                </li>
              </ol>
            </div>

            {/* Fermer */}
            <button
              onClick={dismissIOS}
              className="shrink-0 text-white font-bold text-xl leading-none px-1 py-0.5 opacity-60 hover:opacity-100"
              aria-label="Fermer"
            >
              ×
            </button>
          </div>

          {/* Flèche visuelle vers le bas */}
          <div className="text-center pb-2 opacity-40">
            <span className="text-xl">↓</span>
          </div>
        </div>
      )}

      {/* ===== Bannière Android ===== */}
      {showAndroid && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up"
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.4)",
          }}
        >
          <div className="flex items-center gap-3 px-4 py-3 max-w-lg mx-auto">
            <div className="shrink-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                style={{ backgroundColor: "#e00000" }}
              >
                ⏱
              </div>
            </div>
            <span className="flex-1 text-sm font-bold leading-tight">
              Installez l&apos;application pour un accès rapide
            </span>
            <button
              onClick={handleInstallClick}
              className="shrink-0 text-xs font-bold px-4 py-2 rounded-lg transition-colors duration-150"
              style={{ backgroundColor: "#e00000", color: "#ffffff" }}
            >
              📲 Installer
            </button>
            <button
              onClick={dismissAndroid}
              className="shrink-0 text-white font-bold text-xl leading-none px-1 py-0.5 opacity-60 hover:opacity-100"
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}
